import React from "react";
import { gql } from "apollo-boost";

import StatusBadge from "designV2/components/StatusBadge";
import SettingsList, { enumOptions, humanize } from "superAdminViews/setting/SettingsList";

const ADD = gql`
  mutation createCanceledTripReasonByAdmin(
    $title: String!
    $by: CanceledTripReasonBy!
    $when: CanceledTripReasonWhen!
    $type: CanceledTripReasonType!
  ) {
    createCanceledTripReasonByAdmin(
      input: { title: $title, by: $by, when: $when, type: $type }
    ) {
      _id
      title
    }
  }
`;

const GET = gql`
  query getCanceledTripReasonsByAdmin(
    $limit: Int
    $skip: Int
    $by: CanceledTripReasonBy
    $when: CanceledTripReasonWhen
  ) {
    getCanceledTripReasonsByAdmin(
      pagination: { limit: $limit, skip: $skip }
      filters: { by: $by, when: $when }
    ) {
      _id
      title
      by
      when
      type
    }
  }
`;

const COUNT = gql`
  query getCanceledTripReasonsByAdminCount(
    $by: CanceledTripReasonBy
    $when: CanceledTripReasonWhen
  ) {
    getCanceledTripReasonsByAdminCount(filters: { by: $by, when: $when })
  }
`;

const UPDATE = gql`
  mutation updateCanceledTripReasonByAdmin(
    $id: ID!
    $title: String!
    $when: CanceledTripReasonWhen!
    $by: CanceledTripReasonBy!
    $type: CanceledTripReasonType!
  ) {
    updateCanceledTripReasonByAdmin(
      id: $id
      input: { title: $title, when: $when, by: $by, type: $type }
    ) {
      _id
      title
      when
      by
      type
    }
  }
`;

const DELETE = gql`
  mutation removeCanceledTripReasonByAdmin($idSet: [ID!]!) {
    removeCanceledTripReasonByAdmin(idSet: $idSet) {
      _id
    }
  }
`;

const BY = ["DRIVER", "PASSENGER", "ADMIN"];
const WHEN = ["BEFORE_PICK_UP", "DURING_TRIP"];
const TYPE = ["RIDE", "DELIVERY"];

const FIELDS = [
  { name: "title", label: "Reason", type: "text", required: true },
  {
    name: "by",
    label: "Who cancels",
    type: "select",
    required: true,
    options: enumOptions(BY),
    hint: "Only this side of the trip is offered this reason.",
  },
  {
    name: "when",
    label: "When",
    type: "select",
    required: true,
    options: enumOptions(WHEN),
  },
  {
    name: "type",
    label: "Trip type",
    type: "select",
    required: true,
    options: enumOptions(TYPE),
  },
];

/**
 * CancelTripReason - the reasons offered when a trip is called off.
 *
 * Which reasons appear depends on who is cancelling and how far the
 * trip has got, so the list can be filtered the same two ways —
 * previously the two filters existed in the query but not the screen.
 */
const CancelTripReason = () => (
  <SettingsList
    title="Cancellation reasons"
    description="What a driver, passenger or admin can pick when calling off a trip."
    singular="reason"
    list={{ query: GET, key: "getCanceledTripReasonsByAdmin" }}
    count={{ query: COUNT, key: "getCanceledTripReasonsByAdminCount" }}
    create={{
      mutation: ADD,
      variables: (values) => ({
        title: values.title.trim(),
        by: values.by,
        when: values.when,
        type: values.type,
      }),
    }}
    update={{
      mutation: UPDATE,
      variables: (values, row) => ({
        id: row._id,
        title: values.title.trim(),
        by: values.by,
        when: values.when,
        type: values.type,
      }),
    }}
    remove={{ mutation: DELETE }}
    filters={[
      { name: "by", label: "Cancelled by", options: enumOptions(BY) },
      { name: "when", label: "When", options: enumOptions(WHEN) },
    ]}
    columns={[
      { key: "title", label: "Reason", width: "3fr" },
      { key: "by", label: "Cancelled by", render: (row) => humanize(row.by) },
      { key: "when", label: "When", render: (row) => humanize(row.when) },
      {
        key: "type",
        label: "Trip type",
        render: (row) => (
          <StatusBadge
            status={row.type === "RIDE" ? "info" : "accent"}
            label={humanize(row.type)}
          />
        ),
      },
    ]}
    fields={FIELDS}
    toValues={(row) => ({
      title: row.title,
      by: row.by,
      when: row.when,
      type: row.type,
    })}
  />
);

export default CancelTripReason;
